import { FC, useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  DatePicker,
  Select,
  InputNumber,
  Input,
  TimePicker,
  Divider,
  Tooltip,
  Popconfirm,
  message,
  Spin,
  Empty,
} from "antd";
import {
  Calendar,
  Plus,
  Copy,
  Trash2,
  Users,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import { useGetStaffRoles } from "~/services/common";
import {
  useGenerateExpoDays,
  useSaveExpoDays,
  useCopyExpoDay,
  useDeleteExpoDay,
} from "~/services/estimate";
import { useGetExpoServiceSummary } from "~/services/estimate/estimate.queries";
import {
  ExpoServiceSummary,
  ExpoServiceDayDto,
  ExpoDayPositionDto,
} from "~/types/estimate";

const { RangePicker } = DatePicker;

interface ExpoServiceFormProps {
  estimateServiceId: string;
  onClose?: () => void;
}

// Format currency helper
const formatCurrency = (amount: number): string => {
  return `$${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

export const ExpoServiceForm: FC<ExpoServiceFormProps> = ({
  estimateServiceId,
  onClose,
}) => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [days, setDays] = useState<ExpoServiceDayDto[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([0])); // First day expanded by default
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch existing expo data
  const { data: expoSummary, isLoading: isLoadingSummary } = useGetExpoServiceSummary(
    estimateServiceId,
    true
  );

  // Fetch positions for dropdown
  const { data: staffRolesData } = useGetStaffRoles();
  const positions = staffRolesData?.items || [];

  // Mutations
  const generateDaysMutation = useGenerateExpoDays();
  const saveDaysMutation = useSaveExpoDays();
  const copyDayMutation = useCopyExpoDay();
  const deleteDayMutation = useDeleteExpoDay();

  // Initialize from existing data
  useEffect(() => {
    if (expoSummary && expoSummary.days.length > 0) {
      const convertedDays: ExpoServiceDayDto[] = expoSummary.days.map((day) => ({
        id: day.id,
        serviceDate: day.serviceDate,
        dayLabel: day.dayLabel || undefined,
        notes: day.notes || undefined,
        positions: day.positions.map((pos) => ({
          id: pos.id,
          positionId: pos.positionId,
          quantity: pos.quantity,
          startTime: pos.startTime,
          duration: pos.duration,
          hourlyRate: pos.hourlyRate,
        })),
      }));
      setDays(convertedDays);

      // Set date range from existing data
      if (expoSummary.startDate && expoSummary.endDate) {
        setDateRange([dayjs(expoSummary.startDate), dayjs(expoSummary.endDate)]);
      }
    }
  }, [expoSummary]);

  // Calculate totals
  const totals = useMemo(() => {
    let grandTotal = 0;
    const dayTotals: number[] = [];

    days.forEach((day) => {
      let dayTotal = 0;
      day.positions.forEach((pos) => {
        const rate = pos.hourlyRate || positions.find((p) => p.id === pos.positionId)?.hourlyRate || 0;
        const subtotal = pos.quantity * pos.duration * Number(rate);
        dayTotal += subtotal;
      });
      dayTotals.push(dayTotal);
      grandTotal += dayTotal;
    });

    return { grandTotal, dayTotals };
  }, [days, positions]);

  // Generate days from date range
  const handleGenerateDays = () => {
    if (!dateRange[0] || !dateRange[1]) {
      message.warning("Please select a date range first");
      return;
    }

    const startDate = dateRange[0];
    const endDate = dateRange[1];
    const newDays: ExpoServiceDayDto[] = [];

    let current = startDate;
    let dayNum = 1;

    while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
      // Determine default label
      let label = `Day ${dayNum}`;
      if (dayNum === 1) label = "Setup";
      else if (current.isSame(endDate, "day")) label = "Strike";

      newDays.push({
        serviceDate: current.format("YYYY-MM-DD"),
        dayLabel: label,
        notes: "",
        positions: [],
      });

      current = current.add(1, "day");
      dayNum++;
    }

    setDays(newDays);
    setExpandedDays(new Set([0]));
    setHasChanges(true);
  };

  // Add position to a day
  const addPosition = (dayIndex: number) => {
    const newDays = [...days];
    newDays[dayIndex].positions.push({
      positionId: "",
      quantity: 1,
      startTime: "08:00",
      duration: 8,
    });
    setDays(newDays);
    setHasChanges(true);
  };

  // Update position
  const updatePosition = (
    dayIndex: number,
    posIndex: number,
    field: keyof ExpoDayPositionDto,
    value: any
  ) => {
    const newDays = [...days];
    const position = newDays[dayIndex].positions[posIndex];

    if (field === "positionId") {
      // When position changes, update the hourly rate
      const selectedPosition = positions.find((p) => p.id === value);
      position.positionId = value;
      position.hourlyRate = selectedPosition?.hourlyRate || 0;
    } else {
      (position as any)[field] = value;
    }

    setDays(newDays);
    setHasChanges(true);
  };

  // Remove position
  const removePosition = (dayIndex: number, posIndex: number) => {
    const newDays = [...days];
    newDays[dayIndex].positions.splice(posIndex, 1);
    setDays(newDays);
    setHasChanges(true);
  };

  // Update day field
  const updateDay = (dayIndex: number, field: keyof ExpoServiceDayDto, value: any) => {
    const newDays = [...days];
    (newDays[dayIndex] as any)[field] = value;
    setDays(newDays);
    setHasChanges(true);
  };

  // Copy from Day 1 to all other days
  const copyFromDayOne = () => {
    if (days.length < 2) {
      message.warning("Need at least 2 days to copy");
      return;
    }

    const sourceDay = days[0];
    const newDays = days.map((day, index) => {
      if (index === 0) return day;
      return {
        ...day,
        positions: sourceDay.positions.map((pos) => ({
          ...pos,
          id: undefined, // Remove ID so it creates new positions
        })),
      };
    });

    setDays(newDays);
    setHasChanges(true);
    message.success("Positions copied to all days");
  };

  // Apply to all days
  const applyToAllDays = (sourceDayIndex: number) => {
    const sourceDay = days[sourceDayIndex];
    const newDays = days.map((day, index) => {
      if (index === sourceDayIndex) return day;
      return {
        ...day,
        positions: sourceDay.positions.map((pos) => ({
          ...pos,
          id: undefined,
        })),
      };
    });

    setDays(newDays);
    setHasChanges(true);
    message.success(`Positions from ${sourceDay.dayLabel || `Day ${sourceDayIndex + 1}`} applied to all days`);
  };

  // Delete a day
  const deleteDay = (dayIndex: number) => {
    const newDays = [...days];
    newDays.splice(dayIndex, 1);
    setDays(newDays);
    setHasChanges(true);
  };

  // Toggle day expansion
  const toggleDayExpanded = (dayIndex: number) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dayIndex)) {
      newExpanded.delete(dayIndex);
    } else {
      newExpanded.add(dayIndex);
    }
    setExpandedDays(newExpanded);
  };

  // Save all changes
  const handleSave = async () => {
    // Validate
    for (const day of days) {
      for (const pos of day.positions) {
        if (!pos.positionId) {
          message.error("Please select a position for all entries");
          return;
        }
      }
    }

    try {
      await saveDaysMutation.mutateAsync({
        estimateServiceId,
        days,
      });
      message.success("Expo days saved successfully");
      setHasChanges(false);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save expo days");
    }
  };

  if (isLoadingSummary) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Date Range */}
      <Card className="shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Calendar className="text-blue-500" size={20} />
            <h3 className="text-lg font-semibold">Expo Service Days</h3>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Labor Total</div>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(totals.grandTotal)}
            </div>
          </div>
        </div>

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
              className="w-full"
              size="large"
            />
          </div>
          <Button
            type="primary"
            icon={<Calendar size={16} />}
            onClick={handleGenerateDays}
            size="large"
          >
            Generate Days
          </Button>
        </div>

        {days.length > 1 && (
          <div className="mt-4 pt-4 border-t flex gap-2">
            <Button
              icon={<Copy size={14} />}
              onClick={copyFromDayOne}
            >
              Copy Day 1 to All
            </Button>
          </div>
        )}
      </Card>

      {/* Days List */}
      {days.length === 0 ? (
        <Card className="shadow-sm">
          <Empty
            description="No days configured yet. Select a date range and click Generate Days."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {days.map((day, dayIndex) => {
            const isExpanded = expandedDays.has(dayIndex);
            const dayTotal = totals.dayTotals[dayIndex] || 0;

            return (
              <Card key={dayIndex} className="shadow-sm">
                {/* Day Header */}
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleDayExpanded(dayIndex)}
                >
                  <div className="flex items-center space-x-3">
                    <button className="text-gray-500 hover:text-gray-700">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </button>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold">
                          {dayjs(day.serviceDate).format("ddd, MMM D, YYYY")}
                        </span>
                        <Input
                          value={day.dayLabel}
                          onChange={(e) => updateDay(dayIndex, "dayLabel", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Day label (e.g., Setup)"
                          className="w-32 text-sm"
                          size="small"
                        />
                      </div>
                      <div className="text-sm text-gray-500">
                        {day.positions.length} position{day.positions.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Day Total</div>
                      <div className="font-semibold text-blue-600">
                        {formatCurrency(dayTotal)}
                      </div>
                    </div>
                    <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Apply to All Days">
                        <Button
                          size="small"
                          icon={<Copy size={14} />}
                          onClick={() => applyToAllDays(dayIndex)}
                          disabled={day.positions.length === 0}
                        />
                      </Tooltip>
                      <Popconfirm
                        title="Delete this day?"
                        onConfirm={() => deleteDay(dayIndex)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          size="small"
                          danger
                          icon={<Trash2 size={14} />}
                        />
                      </Popconfirm>
                    </div>
                  </div>
                </div>

                {/* Day Content - Positions */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t">
                    {/* Notes */}
                    <div className="mb-4">
                      <Input.TextArea
                        value={day.notes}
                        onChange={(e) => updateDay(dayIndex, "notes", e.target.value)}
                        placeholder="Day notes (optional)"
                        rows={2}
                        className="text-sm"
                      />
                    </div>

                    {/* Positions Table */}
                    {day.positions.length > 0 && (
                      <div className="space-y-3 mb-4">
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-2">
                          <div className="col-span-4">Position</div>
                          <div className="col-span-2">Qty</div>
                          <div className="col-span-2">Start</div>
                          <div className="col-span-2">Duration</div>
                          <div className="col-span-1">Subtotal</div>
                          <div className="col-span-1"></div>
                        </div>

                        {day.positions.map((pos, posIndex) => {
                          const rate = pos.hourlyRate || positions.find((p) => p.id === pos.positionId)?.hourlyRate || 0;
                          const subtotal = pos.quantity * pos.duration * Number(rate);

                          return (
                            <div
                              key={posIndex}
                              className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded"
                            >
                              <div className="col-span-4">
                                <Select
                                  value={pos.positionId || undefined}
                                  onChange={(value) => updatePosition(dayIndex, posIndex, "positionId", value)}
                                  placeholder="Select position"
                                  className="w-full"
                                  size="small"
                                  showSearch
                                  filterOption={(input, option) =>
                                    (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                                  }
                                  options={positions.map((p) => ({
                                    value: p.id,
                                    label: `${p.name} ($${p.hourlyRate}/hr)`,
                                  }))}
                                />
                              </div>
                              <div className="col-span-2">
                                <InputNumber
                                  value={pos.quantity}
                                  onChange={(value) => updatePosition(dayIndex, posIndex, "quantity", value || 1)}
                                  min={1}
                                  className="w-full"
                                  size="small"
                                />
                              </div>
                              <div className="col-span-2">
                                <TimePicker
                                  value={pos.startTime ? dayjs(pos.startTime, "HH:mm") : undefined}
                                  onChange={(time) =>
                                    updatePosition(dayIndex, posIndex, "startTime", time?.format("HH:mm") || "08:00")
                                  }
                                  format="h:mm A"
                                  className="w-full"
                                  size="small"
                                  minuteStep={15}
                                />
                              </div>
                              <div className="col-span-2">
                                <InputNumber
                                  value={pos.duration}
                                  onChange={(value) => updatePosition(dayIndex, posIndex, "duration", value || 1)}
                                  min={0.5}
                                  step={0.5}
                                  className="w-full"
                                  size="small"
                                  addonAfter="hrs"
                                />
                              </div>
                              <div className="col-span-1 text-right font-medium text-sm">
                                {formatCurrency(subtotal)}
                              </div>
                              <div className="col-span-1 text-right">
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<Trash2 size={14} />}
                                  onClick={() => removePosition(dayIndex, posIndex)}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Button
                      type="dashed"
                      icon={<Plus size={14} />}
                      onClick={() => addPosition(dayIndex)}
                      className="w-full"
                    >
                      Add Position
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Footer with Save */}
      {days.length > 0 && (
        <Card className="shadow-sm bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-lg font-semibold text-blue-900">
                Grand Total: {formatCurrency(totals.grandTotal)}
              </div>
              <div className="text-sm text-blue-700">
                {days.length} day{days.length !== 1 ? "s" : ""} •{" "}
                {days.reduce((sum, d) => sum + d.positions.length, 0)} position assignment
                {days.reduce((sum, d) => sum + d.positions.length, 0) !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="flex gap-2">
              {onClose && (
                <Button size="large" onClick={onClose}>
                  Cancel
                </Button>
              )}
              <Button
                type="primary"
                size="large"
                onClick={handleSave}
                loading={saveDaysMutation.isPending}
                disabled={!hasChanges}
              >
                Save Expo Days
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
